if (!customElements.get('variant-selects')) {

  /**
   *  @class
   *  @function VariantSelects
   *
   *  SL 2026-10-30: producten met meer dan 250 varianten (bv. Hotbath Cobber IBS7009, 360 varianten).
   *  Shopify zet in product.variants maximaal 250 varianten, dus de JSON in de pagina is dan onvolledig.
   *  - setDisabled() koppelt nu op waarde i.p.v. positie (crashte eerst -> prijs/variant werden niet ververst
   *    en de winkelwagen kreeg de oude variant).
   *  - Staat een gekozen combinatie niet in de JSON, dan wordt de variant opgehaald via
   *    ?option_values=...&section_id=... en uit [data-sl-selected-variant] gelezen.
   *  Backup vorige versie: assets/bak-20261030-product.js
   */
  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this.isDisabledFeature = this.dataset.isDisabled;
      this.updateUrl = this.dataset.updateUrl === 'true';
      this.addEventListener('change', this.onVariantChange);

      this.productWrapper = this.closest('.thb-product-detail');
      this.productSlider = this.productWrapper.querySelector('.product-images');
      this.hideVariants = this.dataset.hideVariants === 'true';
    }

    connectedCallback() {
      this.updateOptions();
      this.updateMasterId();
      this.setDisabled();
      this.setImageSet();
    }

    onVariantChange() {
      this.updateOptions();
      this.updateMasterId();
      this.toggleAddButton(true, '', false);
      this.removeErrorMessage();
      this.updateVariantText();
      // SL 2026-10-30: combinatie niet in de (max. 250) varianten-JSON -> bij de server opvragen.
      if (!this.currentVariant && this.isVariantDataTruncated()) {
        this.resolveVariantFromServer();
        return;
      }
      this.applyVariantChange();
    }

    applyVariantChange(prefetchedHtml) {
      this.updatePickupAvailability();
      this.setDisabled();
      if (!this.currentVariant) {
        this.toggleAddButton(true, '', true);
        this.setUnavailable();
      } else {
        this.updateMedia();
        if (this.updateUrl) {
          this.updateURL();
        }
        this.updateVariantInput();
        this.renderProductInfo(prefetchedHtml);
        //this.updateShareUrl();
      }
      dispatchCustomEvent('product:variant-change', {
        variant: this.currentVariant,
        sectionId: this.dataset.section
      });
    }

    isVariantDataTruncated() {
      const data = this.getVariantData();
      return Array.isArray(data) && data.length >= 250;
    }

    optionsMatch(variant, options) {
      if (!variant || !Array.isArray(variant.options) || variant.options.length !== options.length) return false;
      return variant.options.every((value, index) => value === options[index]);
    }

    getInitialSelectedVariant() {
      if (this.initialSelectedVariant !== undefined) return this.initialSelectedVariant;
      const el = this.querySelector('[data-sl-selected-variant]');
      try {
        this.initialSelectedVariant = el ? JSON.parse(el.textContent) : null;
      } catch (e) {
        this.initialSelectedVariant = null;
      }
      return this.initialSelectedVariant;
    }

    getOptionValueIds() {
      return Array.from(this.querySelectorAll('fieldset')).map((fieldset) => {
        const select = fieldset.querySelector('select');
        if (select) {
          const option = select.options[select.selectedIndex];
          return option ? option.dataset.optionValueId : undefined;
        }
        const input = fieldset.querySelector('input:checked');
        return input ? input.dataset.optionValueId : undefined;
      });
    }

    resolveVariantFromServer() {
      const ids = this.getOptionValueIds();
      const options = this.options.slice();
      const requestId = (this.productInfoRequestId || 0) + 1;
      this.productInfoRequestId = requestId;

      if (!ids.length || ids.some((id) => !id)) {
        this.applyVariantChange();
        return;
      }

      const priceContainer = document.getElementById(`price-${this.dataset.section}`);
      if (priceContainer) priceContainer.classList.add('visibility-hidden');

      const isCurrent = () => this.productInfoRequestId === requestId && options.join('|') === (this.options || []).join('|');

      fetch(`${this.dataset.url}?option_values=${ids.join(',')}&section_id=${this.dataset.section}`)
        .then((response) => {
          if (!response.ok) throw new Error('Variant request failed');
          return response.text();
        })
        .then((responseText) => {
          if (!isCurrent()) return;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const el = html.querySelector('[data-sl-selected-variant]');
          let variant = null;
          try {
            variant = el ? JSON.parse(el.textContent) : null;
          } catch (e) {
            variant = null;
          }
          if (this.optionsMatch(variant, options)) {
            this.getVariantData().push(variant);
            this.currentVariant = variant;
          } else {
            this.currentVariant = undefined;
          }
          this.applyVariantChange(html);
        })
        .catch(() => {
          if (!isCurrent()) return;
          this.currentVariant = undefined;
          this.applyVariantChange();
        });
    }

    updateOptions() {
      this.fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = [];
      this.fieldsets.forEach((fieldset, i) => {
        if (fieldset.querySelector('select')) {
          this.options.push(fieldset.querySelector('select').value);
        } else if (fieldset.querySelectorAll('input').length) {
          this.options.push(fieldset.querySelector('input:checked').value);
        }
      });
    }
    updateVariantText() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      fieldsets.forEach((item, i) => {
        let label = item.querySelector('.form__label__value');
        if (label) {
          label.innerHTML = this.options[i];
        }
      });
    }
    updateMasterId() {
      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options.map((option, index) => {
          return this.options[index] === option;
        }).includes(false);
      });
      // SL 2026-10-30: pagina geopend met een variant buiten de eerste 250 -> server-variant gebruiken.
      if (!this.currentVariant && this.isVariantDataTruncated()) {
        const initial = this.getInitialSelectedVariant();
        if (this.optionsMatch(initial, this.options)) {
          this.getVariantData().push(initial);
          this.currentVariant = initial;
        }
      }
    }

    updateMedia() {
      if (!this.currentVariant) return;
      // SL 2026-10-01: variantfoto-groepen (data-set-name="variant") worden door één regeling
      // gestuurd: assets/stonea-variant-media-v2.js (window.SLVariantMedia). Geeft die false terug,
      // dan geldt het originele themagedrag hieronder.
      if (window.SLVariantMedia && window.SLVariantMedia.handle(this)) return;
      if (!this.currentVariant.featured_media) return;

      let productSlider = this.closest('.thb-product-detail').querySelector('.product-images'),
        thumbnails = this.closest('.thb-product-detail').querySelector('#Product-Thumbnails');

      this.setActiveMedia(`#Slide-${this.dataset.section}-${this.currentVariant.featured_media.id}`, `#Thumb-${this.dataset.section}-${this.currentVariant.featured_media.id}`, productSlider, thumbnails);
    }
    setActiveMedia(mediaId, thumbId, productSlider, thumbnails) {
      let flkty = Flickity.data(productSlider),
        activeMedia = productSlider.querySelector(mediaId);

      if (!activeMedia) return;

      if (this.imageSetIndex) {
        this.setImageSetMedia();
      }

      if (flkty && (this.hideVariants || this.imageSetIndex)) {

        if (productSlider.querySelector('.product-images__slide.is-initial-selected')) {
          productSlider.querySelector('.product-images__slide.is-initial-selected').classList.remove('is-initial-selected');
        }
        [].forEach.call(productSlider.querySelectorAll('.product-images__slide-item--variant'), function (el) {
          el.classList.remove('is-active');
        });

        activeMedia.classList.add('is-active');
        activeMedia.classList.add('is-initial-selected');

        if (thumbnails) {
          let activeThumb = thumbnails.querySelector(thumbId);

          if (thumbnails.querySelector('.product-thumbnail.is-initial-selected')) {
            thumbnails.querySelector('.product-thumbnail.is-initial-selected').classList.remove('is-initial-selected');
          }
          [].forEach.call(thumbnails.querySelectorAll('.product-images__slide-item--variant'), function (el) {
            el.classList.remove('is-active');
          });

          activeThumb.classList.add('is-active');
          activeThumb.classList.add('is-initial-selected');
        }

        if (typeof productSlider.reInit === 'function') productSlider.reInit(this.imageSetIndex);
        productSlider.selectCell(mediaId);

      } else if (flkty) {
        productSlider.selectCell(mediaId);
      }

    }

    updateURL() {
      if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateShareUrl() {
      const shareButton = document.getElementById(`Share-${this.dataset.section}`);
      if (!shareButton) return;
      shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateVariantInput() {
      const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment`);
      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', {
          bubbles: true
        }));
      });
    }

    updatePickupAvailability() {
      const pickUpAvailability = document.querySelector('.pickup-availability-wrapper');

      if (!pickUpAvailability) return;

      if (this.currentVariant && this.currentVariant.available) {
        pickUpAvailability.fetchAvailability(this.currentVariant.id);
      } else {
        pickUpAvailability.removeAttribute('available');
        pickUpAvailability.innerHTML = '';
      }
    }

    removeErrorMessage() {
      const section = this.closest('section');
      if (!section) return;

      const productForm = section.querySelector('product-form');
      if (productForm) productForm.handleErrorMessage();
    }

    getSectionsToRender() {
      return [`price-${this.dataset.section}`, `inventory-${this.dataset.section}`, `sku-${this.dataset.section}`];
    }

    renderProductInfo(prefetchedHtml) {
      const variant = this.currentVariant;
      if (!variant) return;

      const requestId = (this.productInfoRequestId || 0) + 1;
      this.productInfoRequestId = requestId;
      const sectionId = this.dataset.section;
      const priceContainer = document.getElementById(`price-${sectionId}`);

      if (priceContainer) {
        priceContainer.classList.add('visibility-hidden');
        // Clear the previous variant price immediately so it cannot flash while the new section loads.
        priceContainer.querySelectorAll('.price .amount').forEach((amount) => {
          amount.textContent = '';
        });
      }

      // SL 2026-10-30: sectie al opgehaald bij het opzoeken van de variant -> direct gebruiken.
      if (prefetchedHtml) {
        this.applyProductInfo(prefetchedHtml, variant);
        return;
      }

      fetch(`${this.dataset.url}?variant=${variant.id}&section_id=${sectionId}`)
        .then((response) => {
          if (!response.ok) throw new Error('Product information request failed');
          return response.text();
        })
        .then((responseText) => {
          if (
            this.productInfoRequestId !== requestId ||
            !this.currentVariant ||
            String(this.currentVariant.id) !== String(variant.id)
          ) return;

          const html = new DOMParser().parseFromString(responseText, 'text/html');
          this.applyProductInfo(html, variant);
        })
        .catch(() => {
          if (this.productInfoRequestId !== requestId) return;
          this.toggleAddButton(true, '', false);
        });
    }

    applyProductInfo(html, variant) {
      const sectionId = this.dataset.section;
      const sections = this.getSectionsToRender();
      const priceId = `price-${sectionId}`;
      const priceContainer = document.getElementById(priceId);
      let hasValidPrice = !priceContainer;

      sections.forEach((id) => {
        const destination = document.getElementById(id);
        const source = html.getElementById(id);
        if (!source || !destination) return;

        if (id === priceId) {
          const amount = source.querySelector('.price .amount');
          if (!amount || !amount.textContent.trim()) return;
          hasValidPrice = true;
        }

        destination.innerHTML = source.innerHTML;
      });

      if (this.isVariantDataTruncated()) this.syncOptionStates(html);

      if (!hasValidPrice) return;
      if (priceContainer) priceContainer.classList.remove('visibility-hidden');
      this.toggleAddButton(!variant.available, window.theme.variantStrings.soldOut);
    }

    // SL 2026-10-30: bij >250 varianten de beschikbaarheid (doorgestreept) uit de server-sectie overnemen.
    syncOptionStates(html) {
      if (this.isDisabledFeature != 'true' || !this.id) return;
      const source = html.getElementById(this.id);
      if (!source) return;
      const sourceFieldsets = Array.from(source.querySelectorAll('fieldset'));
      Array.from(this.querySelectorAll('fieldset')).forEach((fieldset, i) => {
        const sourceFieldset = sourceFieldsets[i];
        if (!sourceFieldset) return;
        const sourceInputs = Array.from(sourceFieldset.querySelectorAll('input'));
        fieldset.querySelectorAll('input').forEach((input) => {
          const match = sourceInputs.find((el) => el.value === input.value);
          if (match) input.classList.toggle('is-disabled', match.classList.contains('is-disabled'));
        });
        const sourceOptions = Array.from(sourceFieldset.querySelectorAll('option'));
        fieldset.querySelectorAll('option').forEach((option) => {
          const match = sourceOptions.find((el) => el.value === option.value);
          if (match) option.disabled = match.disabled;
        });
      });
    }

    toggleAddButton(disable = true, text = false, modifyClass = true) {
      const productForm = document.getElementById(`product-form-${this.dataset.section}`);
      if (!productForm) return;

      const productTemplate = productForm.closest('.product-form').getAttribute('template');
      const addButton = productForm.querySelector('[name="add"]');
      const addButtonText = productForm.querySelector('[name="add"] > span:not(.atc-icon):not(.atc-chevron):not(.loading-overlay)');

      if (!addButton) return;

      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        addButton.classList.remove('loading');
        addButtonText.textContent = window.theme.variantStrings.addToCart;

        if (productTemplate?.includes('pre-order')) {
          addButtonText.textContent = window.theme.variantStrings.preOrder;
        } else {
          addButtonText.textContent = window.theme.variantStrings.addToCart;
        }
      }
      if (!modifyClass) return;
    }

    setUnavailable() {
      const button = document.getElementById(`product-form-${this.dataset.section}`);
      const addButton = button.querySelector('[name="add"]');
      const addButtonText = button.querySelector('[name="add"] > span:not(.atc-icon):not(.atc-chevron):not(.loading-overlay)');
      const price = document.getElementById(`price-${this.dataset.section}`);
      if (!addButton) return;
      addButtonText.textContent = window.theme.variantStrings.unavailable;
      addButton.classList.add('sold-out');
      if (price) price.classList.add('visibility-hidden');
    }

    setDisabled() {
      if (this.isDisabledFeature != 'true') {
        return;
      }
      // SL 2026-10-30: onvolledige varianten-JSON -> niet lokaal berekenen (zou opties ten onrechte
      // doorstrepen); de stand komt uit de server-sectie (syncOptionStates).
      if (this.isVariantDataTruncated()) {
        return true;
      }
      const variant_data = this.getVariantData();


      if (variant_data) {

        let selected_options = false;
        if (this.currentVariant) {
          selected_options = this.currentVariant.options.map((value, index) => {
            return {
              value,
              index: `option${index + 1}`
            };
          });
        } else {
          let found_option = variant_data.find(option => {
            return option.option1 === this.options[0];
          });
          if (found_option) {
            selected_options = [
              {
                "value": this.options[0],
                "index": "option1"
              },
              {
                "value": found_option.option2,
                "index": "option2"
              }
            ];
          } else {
            return;
          }
        }
        const available_options = this.createAvailableOptionsTree(variant_data, selected_options);

        this.fieldsets.forEach((fieldset, i) => {
          const fieldset_options = Object.values(available_options)[i];

          if (fieldset_options) {
            if (fieldset.querySelector('select')) {
              fieldset_options.forEach((option, option_i) => {
                const optionEl = fieldset.querySelector('option[value=' + JSON.stringify(option.value) + ']');
                if (optionEl) optionEl.disabled = !!option.isUnavailable;
              });
            } else if (fieldset.querySelectorAll('input').length) {
              // SL 2026-10-30: koppelen op waarde i.p.v. positie (positie kon ontbreken -> TypeError).
              fieldset.querySelectorAll('input').forEach((input) => {
                const option = fieldset_options.find((entry) => entry.value === input.value);
                if (option) input.classList.toggle('is-disabled', !!option.isUnavailable);
              });
            }
          }
        });

      }
      return true;
    }

    getImageSetName(variant_name) {
      return variant_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '').replace(/^-/, '');
    }

    setImageSet() {
      if (!this.productSlider) return;

      let dataSetEl = this.productSlider.querySelector('[data-set-name]');
      if (dataSetEl) {
        this.imageSetName = dataSetEl.dataset.setName;
        if (this.imageSetName === 'variant') {
          this.imageSetIndex = 'variant';
        } else {
          const imageSetInput = this.querySelector('.product-form__input[data-handle="' + this.imageSetName + '"]');
          if (!imageSetInput) return;
          this.imageSetIndex = imageSetInput.dataset.index;
        }
        this.dataset.imageSetIndex = this.imageSetIndex;
        // SL 2026-10-01: SLVariantMedia regelt foto's + Flickity zelf; dan geen tweede reInit hier.
        if (window.SLVariantMedia && window.SLVariantMedia.handle(this)) return;
        this.setImageSetMedia();
        if (typeof this.productSlider.reInit === 'function') this.productSlider.reInit();
      }
    }
    setImageSetMedia() {
      if (!this.imageSetIndex) {
        return;
      }
      // SL 2026-10-01: niet tegelijk met SLVariantMedia dezelfde klassen aanpassen.
      if (window.SLVariantMedia && window.SLVariantMedia.handle(this)) return;
      if (!this.currentVariant) return;

      let group;
      if (this.imageSetName === 'variant') {
        group = 'variant_' + this.currentVariant.id;
      } else {
        let setValue = this.getImageSetName(this.currentVariant[this.imageSetIndex]);
        group = this.imageSetName + '_' + setValue;
      }
      let selected_set_images = this.productWrapper.querySelectorAll(`.product-images__slide[data-set-name="${this.imageSetName}"]`),
        selected_set_thumbs = this.productWrapper.querySelectorAll(`.product-thumbnail[data-set-name="${this.imageSetName}"]`);

      if (this.hideVariants || this.imageSetIndex) {
        // Product images
        this.productWrapper.querySelectorAll('.product-images__slide').forEach(thumb => {
          if (thumb.dataset.group && thumb.dataset.group !== group) {
            thumb.classList.remove('is-active');
          }
        });
        selected_set_images.forEach(thumb => {
          thumb.classList.toggle('is-active', thumb.dataset.group === group);
        });

        // Product thumbnails
        this.productWrapper.querySelectorAll('.product-thumbnail').forEach(thumb => {
          if (thumb.dataset.group && thumb.dataset.group !== group) {
            thumb.classList.remove('is-active');
          }
        });
        selected_set_thumbs.forEach(thumb => {
          thumb.classList.toggle('is-active', thumb.dataset.group === group);
        });
      }

    }

    createAvailableOptionsTree(variant_data, selected_options) {
      // Reduce variant array into option availability tree
      return variant_data.reduce((options, variant) => {

        // Check each option group (e.g. option1, option2, option3) of the variant
        Object.keys(options).forEach(index => {

          if (variant[index] === null) return;

          let entry = options[index].find(option => option.value === variant[index]);

          if (typeof entry === 'undefined') {
            // If option has yet to be added to the options tree, add it
            entry = {
              value: variant[index],
              isUnavailable: true
            };
            options[index].push(entry);
          }

          // Check how many selected option values match a variant
          const countVariantOptionsThatMatchCurrent = selected_options.reduce((count, {
            value,
            index
          }) => {
            return variant[index] === value ? count + 1 : count;
          }, 0);

          // Only enable an option if an available variant matches all but one current selected value
          if (countVariantOptionsThatMatchCurrent >= selected_options.length - 1) {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }

          // Make sure if a variant is unavailable, disable currently selected option
          if ((!this.currentVariant || !this.currentVariant.available) && selected_options.find((option) => option.value === entry.value && index === option.index)) {
            entry.isUnavailable = true;
          }

          // First option is always enabled
          if (index === 'option1') {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }
        });

        return options;
      }, {
        option1: [],
        option2: [],
        option3: []
      });
    }

    getVariantData() {
      this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
      return this.variantData;
    }
  }
  customElements.define('variant-selects', VariantSelects);

  /**
   *  @class
   *  @function VariantRadios
   */
  class VariantRadios extends VariantSelects {
    constructor() {
      super();
    }

    updateOptions() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = fieldsets.map((fieldset) => {
        return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
      });
    }
    updateVariantText() {


    }
  }

  customElements.define('variant-radios', VariantRadios);
}
if (!customElements.get('product-slider')) {
  /**
   *  @class
   *  @function ProductSlider
   */
  class ProductSlider extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('change', this.setupProductGallery);
    }
    connectedCallback() {
      this.product_container = this.closest('.thb-product-detail');
      this.thumbnail_container = this.product_container.querySelector('.product-thumbnail-container');
      this.video_containers = this.querySelectorAll('.product-single__media-external-video--play');

      // SL 2026-10-01: originele volgorde van de foto's vastleggen (zie restoreSlideOrder).
      this.querySelectorAll('.product-images__slide').forEach((slide, index) => {
        if (slide.dataset.slOrder === undefined) slide.dataset.slOrder = String(index);
      });

      this.setOptions();
      // Start Slider
      this.init();
    }
    setOptions() {
      this.hide_variants = this.dataset.hideVariants == 'true';
      if (this.thumbnail_container) {
        this.thumbnails = this.thumbnail_container.querySelectorAll('.product-thumbnail');
      }
      this.prev_button = this.querySelector('.flickity-prev');
      this.next_button = this.querySelector('.flickity-next');
      this.options = {
        wrapAround: true,
        pageDots: true,
        contain: true,
        adaptiveHeight: true,
        initialIndex: '.is-initial-selected',
        prevNextButtons: false,
        fade: false,
        cellSelector: '.product-images__slide.is-active'
      };

      if (this.classList.contains('product-quick-images')) {
        this.options.cellAlign = 'left';
        this.options.pageDots = false;
        this.options.freeScroll = true;
        this.options.wrapAround = false;
      }
    }
    init() {
      this.flkty = new Flickity(this, this.options);

      this.selectedIndex = this.flkty.selectedIndex;

      // Setup Events
      this.setupEvents();

      // Start Gallery
      this.setupProductGallery();
    }
    reInit() {
      this.flkty.destroy();

      // SL 2026-10-01: Flickity.destroy() zet de oude cellen achteraan terug in de DOM.
      // Originele volgorde herstellen, anders verschuift de volgorde na elke variantwissel.
      this.restoreSlideOrder();

      this.setOptions();

      this.flkty = new Flickity(this, this.options);

      // Setup Events
      this.setupEvents();

      this.selectedIndex = this.flkty.selectedIndex;
    }
    restoreSlideOrder() {
      const slides = Array.from(this.children).filter((el) => el.classList.contains('product-images__slide'));
      if (slides.length < 2 || slides.some((slide) => slide.dataset.slOrder === undefined)) return;
      slides.sort((a, b) => Number(a.dataset.slOrder) - Number(b.dataset.slOrder));
      this.prepend(...slides);
    }
    setupEvents() {
      const _this = this;
      if (this.prev_button) {
        let prev = this.prev_button.cloneNode(true);
        this.prev_button.parentNode.append(prev);
        this.prev_button.remove();
        prev.addEventListener('click', (event) => {
          this.flkty.previous();
        });
        prev.addEventListener('keyup', (event) => {
          this.flkty.previous();
          event.preventDefault();
        });
      }
      if (this.next_button) {
        let next = this.next_button.cloneNode(true);
        this.next_button.parentNode.append(next);
        this.next_button.remove();
        next.addEventListener('click', (event) => {
          this.flkty.next();
        });
        next.addEventListener('keyup', (event) => {
          this.flkty.next();
          event.preventDefault();
        });
      }
      this.flkty.on('settle', function (index) {
        _this.selectedIndex = index;
      });
      this.flkty.on('change', (index) => {

        let previous_slide = this.flkty.cells[_this.selectedIndex].element,
          previous_media = previous_slide.querySelector('.product-single__media'),
          slide = this.flkty.cells[index].element,
          media = slide.querySelector('.product-single__media'),
          active_thumb = false;

        if (this.thumbnail_container) {
          let active_thumbs = Array.from(this.thumbnails).filter(element => element.classList.contains('is-active')),
            thumb_id = slide.id ? slide.id.replace(/^Slide-/, 'Thumb-') : '',
            matching_thumb = thumb_id ? document.getElementById(thumb_id) : null;

          active_thumb = matching_thumb && matching_thumb.classList.contains('is-active')
            ? matching_thumb
            : (active_thumbs[index] ? active_thumbs[index] : active_thumbs[0]);

          this.thumbnails.forEach((item, i) => {
            item.classList.remove('is-initial-selected');
          });
          if (active_thumb) {
            active_thumb.classList.add('is-initial-selected');
          }
        }

        requestAnimationFrame(() => {
          if (active_thumb) {
            if (active_thumb.offsetParent === null) {
              return;
            }
            const windowHalfHeight = active_thumb.offsetParent.clientHeight / 2,
              windowHalfWidth = active_thumb.offsetParent.clientWidth / 2;
            active_thumb.parentElement.scrollTo({
              left: active_thumb.offsetLeft - windowHalfWidth + active_thumb.clientWidth / 2,
              top: active_thumb.offsetTop - windowHalfHeight + active_thumb.clientHeight / 2,
              behavior: 'smooth'
            });
          }

        });

        // Stop previous video
        if (previous_media.classList.contains('product-single__media-external-video')) {
          if (previous_media.dataset.provider === 'youtube') {
            previous_media.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
              event: "command",
              func: "pauseVideo",
              args: ""
            }), "*");
          } else if (previous_media.dataset.provider === 'vimeo') {
            previous_media.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
              method: "pause"
            }), "*");
          }
        } else if (previous_media.classList.contains('product-single__media-native-video')) {
          previous_media.querySelector("video").pause();
        }

        // Draggable.
        if (media.classList.contains('product-single__media-model')) {
          this.setDraggable(false);
        } else {
          this.setDraggable(true);
        }
      });

      if (this.thumbnail_container && !this.thumbnailClickHandler) {
        this.thumbnailClickHandler = (event) => {
          const thumbnail = event.target.closest('.product-thumbnail');
          if (!thumbnail || !this.thumbnail_container.contains(thumbnail) || thumbnail.clientWidth === 0) {
            return;
          }
          this.thumbnailClick(thumbnail);
        };
        this.thumbnail_container.addEventListener('click', this.thumbnailClickHandler);
      }
      let scrollbar = document.querySelector('.product-quick-images__scrollbar>div');

      if (scrollbar) {
        this.flkty.on('scroll', function (progress) {
          progress = Math.max(0, Math.min(1, progress));
          scrollbar.style.transform = 'scaleX(' + progress + ')';
        });
      }

    }
    thumbnailClick(thumbnail) {
      [].forEach.call(this.thumbnails, function (el) {
        el.classList.remove('is-initial-selected');
      });
      thumbnail.classList.add('is-initial-selected');
      const slide_id = thumbnail.id.replace(/^Thumb-/, 'Slide-');
      const slide = document.getElementById(slide_id);
      if (slide) {
        this.flkty.selectCell(slide);
      }
    }
    setDraggable(draggable) {
      this.flkty.options.draggable = draggable;
      this.flkty.updateDraggable();
    }
    selectCell(mediaId) {
      this.flkty.selectCell(mediaId);
    }
    setupProductGallery() {
      if (!this.querySelectorAll('.product-single__media-zoom').length) {
        return;
      }
      this.setEventListeners();
    }
    buildItems() {
      this.activeImages = Array.from(this.querySelectorAll('.product-images__slide.is-active .product-single__media-image'));

      return this.activeImages.map((item) => {
        let index = [].indexOf.call(item.parentNode.parentNode.children, item.parentNode);

        let activelink = item.querySelector('.product-single__media-zoom');

        activelink.dataset.index = index;
        return {
          src: activelink.getAttribute('href'),
          msrc: activelink.dataset.msrc,
          w: activelink.dataset.w,
          h: activelink.dataset.h
        };
      });
    }
    setEventListeners() {
      this.links = this.querySelectorAll('.product-single__media-zoom');
      this.pswpElement = document.querySelectorAll('.pswp')[0];
      this.pswpOptions = {
        maxSpreadZoom: 2,
        loop: false,
        allowPanToNext: false,
        closeOnScroll: false,
        showHideOpacity: false,
        arrowKeys: true,
        history: false,
        captionEl: false,
        fullscreenEl: false,
        zoomEl: false,
        shareEl: false,
        counterEl: false,
        arrowEl: true,
        preloaderEl: true,
        getThumbBoundsFn: () => {
          const thumbnail = this.querySelector('.product-images__slide.is-selected'),
            pageYScroll = window.pageYOffset || document.documentElement.scrollTop,
            rect = thumbnail.getBoundingClientRect();
          return {
            x: rect.left,
            y: rect.top + pageYScroll,
            w: rect.width
          };
        }
      };


      this.links.forEach((link => {
        link.addEventListener('click', (e) => this.zoomClick(e, link));
      }));
    }
    zoomClick(e, link) {
      this.items = this.buildItems();
      this.pswpOptions.index = parseInt(link.dataset.index, 10);
      if (typeof PhotoSwipe !== 'undefined') {
        let pswp = new PhotoSwipe(this.pswpElement, PhotoSwipeUI_Default, this.items, this.pswpOptions);
        pswp.listen('firstUpdate', function () {
          pswp.listen('parseVerticalMargin', function (item) {
            item.vGap = {
              top: 50,
              bottom: 50
            };
          });
        });
        pswp.init();
      }
      e.preventDefault();
    }
  }
  customElements.define('product-slider', ProductSlider);
}

/**
 *  @class
 *  @function ProductForm
 */
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cartNotification = document.querySelector('cart-notification');
      this.body = document.body;

      this.hideErrors = this.dataset.hideErrors === 'true';
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (!this.form.reportValidity()) {
        return;
      }
      const submitButton = this.querySelector('[type="submit"]');
      if (submitButton.classList.contains('loading')) return;

      this.handleErrorMessage();

      submitButton.setAttribute('aria-disabled', true);
      submitButton.classList.add('loading');

      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        }
      };
      const formData = new FormData(this.form);
      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      fetch(`${theme.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            dispatchCustomEvent('product:variant-error', {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.description,
              message: response.message
            });
            if (response.status === 422) {
              document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                bubbles: true
              }));
            }
            this.handleErrorMessage(response.description);
            return;
          }

          this.renderContents(response);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
        });
    }

    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section => {
        if (!document.getElementById(section.id)) {
          return;
        }
        const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        if (typeof CartDrawer !== 'undefined') {
          new CartDrawer();
        }
        if (typeof Cart !== 'undefined') {
          new Cart().renderContents(parsedState);
        }
      }));


      if (document.getElementById('Cart-Drawer')) {
        document.getElementById('Cart-Drawer').open();
      }

      let product_drawer = document.getElementById('Product-Drawer');
      if (product_drawer && product_drawer.contains(this)) {
        product_drawer.classList.remove('active');

        if (!document.getElementById('Cart-Drawer')) {
          this.body.classList.remove('open-cc');
        }
      }
    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}

if (typeof addIdToRecentlyViewed !== "undefined") {
  addIdToRecentlyViewed();
}